import { BreadcrumbsData, PrimaryButton, Tooltip } from '@systemeio/ui'
import { BLOGS_API } from 'modules/blogs/api/blog-api'
import BlogCategories from 'modules/blogs/components/blog-categories'
import BlogPages from 'modules/blogs/components/blog-pages'
import BlogPosts from 'modules/blogs/components/blog-posts'
import BlogSettings from 'modules/blogs/components/blog-settings'
import { BlogLoco } from 'modules/blogs/constants/blog-loco'
import { BlogSectionsEnum, BlogSectionsTitleEnum } from 'modules/blogs/enums/blog-sections-enum'
import { useBlog } from 'modules/blogs/hooks/use-blog'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { PageTab } from 'shared/components/page-tab/page-tab'
import { PageTabGroup } from 'shared/components/page-tab/page-tab-group'
import { PageTabList } from 'shared/components/page-tab/page-tab-list'
import { PageTabPanel } from 'shared/components/page-tab/page-tab-panel'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getPageEditorUrl } from 'shared/utils/get-page-editor-url'
import { BlogLayoutIcon, EyeIcon } from '@systemeio/ui/icons'

const BlogEdit: NextPage = () => {
  const router = useRouter()
  const { t } = useLocoTranslation()
  const { blogId, section } = router.query

  const updateData = useUpdateData({
    constantApi: [`${BLOGS_API}/list`, `${BLOGS_API}/${blogId}`],
    api: BLOGS_API,
  })

  const { blog, blogMutate } = useBlog({
    id: blogId as string,
  })

  const breadcrumbsData: BreadcrumbsData[] = [
    { caption: t('dashboard.blog.title_plural'), href: '/blogs', isStatic: true },
    { caption: blog?.name || '' },
    {
      caption: t(BlogSectionsTitleEnum[section as keyof typeof BlogSectionsTitleEnum]),
      isStatic: true,
    },
  ]

  return (
    <>
      <div className={`flex flex-col gap-5`}>
        <div className="flex items-center justify-between gap-5">
          <BreadcrumbsDashboard data={breadcrumbsData} isFetching={!blog} />
          <div className={'flex gap-5 whitespace-nowrap'}>
            <Tooltip mode={'hover'} label={t('dashboard.blog.blog_layout_description')}>
              <PrimaryButton
                as={LinkWithoutPrefetch}
                href={blog && getPageEditorUrl(blog.layoutPage.id)}
                aria-disabled={!blog}
              >
                <BlogLayoutIcon />
                <span className={'hidden lg:flex'}>{t('dashboard.blog.blog_layout')}</span>
              </PrimaryButton>
            </Tooltip>
            <PrimaryButton
              as={LinkWithoutPrefetch}
              target={'_blank'}
              aria-disabled={!blog}
              href={`${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${blog?.domain.domainName}/${blog?.path}`}
            >
              <EyeIcon className={'fill-background-secondary'} />
              <span className={'hidden lg:flex'}>{t('dashboard.blog.view_blog')}</span>
            </PrimaryButton>
            <BlogSettings blog={blog} updateData={updateData} blogMutate={blogMutate} />
          </div>
        </div>

        <PageTabGroup
          selectedIndex={Object.values(BlogSectionsEnum).findIndex(el => el === section)}
        >
          <PageTabList
            buttonBar={
              section === BlogSectionsEnum.posts && (
                <Tooltip
                  popoverClassName={'max-w-[350px]'}
                  className={'pr-4 sm:pr-0'}
                  mode={'hover'}
                  label={t('dashboard.blog.posts.post_layout_description')}
                >
                  <PrimaryButton
                    as={LinkWithoutPrefetch}
                    href={blog && getPageEditorUrl(blog.postLayoutPage.id)}
                    aria-disabled={!blog}
                  >
                    <BlogLayoutIcon />
                    <span className={'hidden lg:flex'}>
                      {t('dashboard.blog.posts.post_layout')}
                    </span>
                  </PrimaryButton>
                </Tooltip>
              )
            }
          >
            <PageTab
              onClick={() =>
                router.push({
                  query: { blogId: blogId, section: BlogSectionsEnum.posts },
                })
              }
            >
              {t('dashboard.blog.posts.title_plural')}
            </PageTab>
            <PageTab
              onClick={() =>
                router.push({
                  query: { blogId: blogId, section: BlogSectionsEnum.pages },
                })
              }
            >
              {t('dashboard.blog.pages.title_plural')}
            </PageTab>
            <PageTab
              onClick={() =>
                router.push({
                  query: { blogId: blogId, section: BlogSectionsEnum.categories },
                })
              }
            >
              {t('dashboard.blog.categories.title_plural')}
            </PageTab>
          </PageTabList>

          <PageTabPanel>
            <BlogPosts blogId={blogId as string} blog={blog} />
          </PageTabPanel>
          <PageTabPanel>
            <BlogPages blogId={blogId as string} blog={blog} />
          </PageTabPanel>
          <PageTabPanel>
            <BlogCategories blogId={blogId as string} blog={blog} />
          </PageTabPanel>
        </PageTabGroup>
      </div>
    </>
  )
}

export const getServerSideProps = getBaseServerSideProps(BlogLoco, 'header.top_menu.blogs')

export default BlogEdit
